// @ts-nocheck
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
    Edit, Trash2, Info, Mail,
    ArrowRight, Calendar, MoreVertical, Plus, Search, X,
    BarChart3,
} from "lucide-react";
import { SkeletonSwitch } from "skeleton";
import { CampaignCardGridSkeleton } from "skeleton/campaign/CampaignSkeleton";
import CampaignStatsDialog, { getStatusStyle } from "./CampaignStatsDialog";
import { EmptyState } from "components/ui/empty-state";
import { ErrorState } from "components/ui/error-state";

const formatNumber = (num) => {
    if (num == null) return "—";
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return String(num);
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
        return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch { return "—"; }
};

/* Column counts must match the number of children VISIBLE at each breakpoint:
   base 4 (check, dot, name, actions) · sm +template=5 · md +credits=6 ·
   xl +dates=7 · 2xl 7.

   `xl` used to declare 6 while Dates turns on at xl, so from 1280px to 1536px
   the seventh child had no column and wrapped: the kebab menu dropped onto a
   second line under the checkbox and the ACTIONS header went with it. */
const LIST_ROW_GRID_CLASS = `grid items-center gap-x-4 gap-y-2 pl-5 pr-4
    grid-cols-[auto_auto_minmax(0,1fr)_auto]
    sm:grid-cols-[auto_auto_minmax(180px,1.5fr)_minmax(130px,1fr)_auto]
    md:grid-cols-[auto_auto_minmax(220px,1.5fr)_minmax(140px,1fr)_minmax(80px,1fr)_48px]
    xl:grid-cols-[auto_auto_minmax(240px,1.5fr)_minmax(140px,1fr)_minmax(72px,auto)_minmax(190px,1fr)_48px]
    2xl:grid-cols-[auto_auto_minmax(280px,1.5fr)_minmax(160px,1fr)_minmax(80px,1fr)_minmax(240px,1fr)_48px]
    2xl:gap-x-8 xl:gap-x-6`;


/*
 * The previous empty state ended in a decoy: a div styled exactly like the
 * primary CTA — same gradient, same size — carrying the text "Use 'Create
 * Campaign' above to get started", with `cursor-default select-none` so it
 * could not be clicked. Something that looks like the button you need and does
 * nothing is worse than no button, so it is now the real action.
 */
function CampaignsEmptyState({ onCreate }: { onCreate: () => void }) {
    return (
        <EmptyState
            icon={Mail}
            title="No campaigns yet"
            description="Create your first email campaign to start reaching your audience."
            primaryAction={{
                label: "Create campaign",
                onClick: onCreate,
                icon: <Plus className="w-4 h-4" />,
            }}
        />
    );
}

export default function CampaignView({
    campaignData,
    campaignLoading,
    selectedCampaigns,
    onCampaignSelect,
    onEdit,
    onDelete,
    onDuplicate,
    onPause,
    onResume,
    onViewDetails,
    onConfigureAI,
    onViewABTests,
    onCreateABTest,
    aiAgents = [],
    abTests = [],
    viewMode = "grid",
    searchTerm: externalSearchTerm,
    /** Supplied by the parent when the search is controlled there */
    onClearSearch,
    /** Query failure, so a failed request is not shown as an empty list */
    isError,
    error,
    onRetry,
}) {
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const [stableMinHeight, setStableMinHeight] = useState(0);
    const [internalSearch, setInternalSearch] = useState("");
    const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearch;

    // ── Stats overlay state ──
    const [statsCampaign, setStatsCampaign] = useState(null);

    const openStats = useCallback((campaign) => setStatsCampaign(campaign), []);
    const closeStats = useCallback(() => setStatsCampaign(null), []);

    useEffect(() => {
        if (containerRef.current) {
            const h = containerRef.current.offsetHeight;
            if (h > 0) setStableMinHeight(h);
        }
    }, [viewMode]);

    const filteredItems = useMemo(() => {
        const items = campaignData?.items ?? [];
        if (!searchTerm.trim()) return items;
        const q = searchTerm.toLowerCase().trim();
        return items.filter((c) =>
            (c.name || "").toLowerCase().includes(q) ||
            (c.c_status || "").toLowerCase().includes(q) ||
            (c.email_used || "").toLowerCase().includes(q) ||
            (c.temp_name || "").toLowerCase().includes(q)
        );
    }, [campaignData?.items, searchTerm]);

    const isEmptyBase = (campaignData?.items ?? []).length === 0;
    const isNoMatch = !isEmptyBase && filteredItems.length === 0;

    return (
        <>
            <div ref={containerRef} style={{ minHeight: campaignLoading ? (stableMinHeight || undefined) : undefined }}>
                <SkeletonSwitch
                    isLoading={campaignLoading}
                    skeleton={<CampaignCardGridSkeleton viewMode={viewMode} />}
                >
                    {/* Inline search bar */}
                    {!isEmptyBase && externalSearchTerm === undefined && (
                        <div className="relative mb-4">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle pointer-events-none" />
                            <input
                                type="text"
                                value={internalSearch}
                                onChange={(e) => setInternalSearch(e.target.value)}
                                placeholder="Search by name, status, email or template…"
                                className="w-full pl-9 pr-8 py-2 text-[13px] border border-line dark:border-dark-600
                                           bg-surface dark:bg-dark-800 text-content dark:text-gray-100
                                           placeholder-gray-400 dark:placeholder-gray-500 rounded-xl
                                           focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 outline-none transition-all shadow-sm"
                            />
                            {internalSearch && (
                                <button
                                    onClick={() => setInternalSearch("")}
                                    aria-label="Clear search"
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full
                                               text-content-subtle hover:text-gray-700 dark:hover:text-gray-200
                                               hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    )}

                    {isError ? (
                        /* Failure and emptiness are different answers: without
                           this the page said "No campaigns yet" whenever the
                           request failed. */
                        <ErrorState
                            title="Couldn't load your campaigns"
                            description="The request didn't complete. Your campaigns are safe — this is only the view."
                            error={error}
                            onRetry={onRetry}
                        />
                    ) : isEmptyBase ? (
                        <CampaignsEmptyState onCreate={() => navigate("/campaigns/new")} />
                    ) : isNoMatch ? (
                        <EmptyState
                            variant="no-results"
                            title={
                                searchTerm
                                    ? `No campaigns match "${searchTerm}"`
                                    : "No campaigns match your filters"
                            }
                            description="Try a different name, status, email or template."
                            primaryAction={
                                onClearSearch
                                    ? { label: "Clear search", onClick: onClearSearch }
                                    : null
                            }
                        />
                    ) : (
                        <div className={
                            viewMode === "grid"
                                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                                : "flex flex-col gap-2"
                        }>
                            {viewMode === "list" && (
                                <div
                                    className={`${LIST_ROW_GRID_CLASS} hidden sm:grid pb-1 text-[10px] font-bold uppercase tracking-wider text-content-subtle dark:text-gray-500`}
                                >
                                    <span aria-hidden="true" />
                                    <span aria-hidden="true" />
                                    <span>Campaign</span>
                                    <span className="text-center">Template</span>
                                    <span className="hidden md:block text-right pr-1">Credits</span>
                                    <span className="hidden xl:block text-center">Dates</span>
                                    <span className="justify-self-center">Actions</span>
                                </div>
                            )}
                            {filteredItems.map((campaign) => (
                                <CampaignCard
                                    key={campaign.id}
                                    campaign={campaign}
                                    isSelected={selectedCampaigns?.has(campaign.id)}
                                    onSelect={() => onCampaignSelect(campaign.id)}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                    onDuplicate={onDuplicate}
                                    onPause={onPause}
                                    onResume={onResume}
                                    onViewDetails={onViewDetails}
                                    onViewStats={openStats}
                                    onConfigureAI={onConfigureAI}
                                    onViewABTests={onViewABTests}
                                    onCreateABTest={onCreateABTest}
                                    aiAgents={aiAgents}
                                    abTests={abTests}
                                    viewMode={viewMode}
                                />
                            ))}
                        </div>
                    )}
                </SkeletonSwitch>
            </div>

            <CampaignStatsDialog campaign={statsCampaign} onClose={closeStats} />
        </>
    );
}

const CampaignCard = ({
    campaign,
    isSelected,
    onSelect,
    onEdit,
    onDelete,
    onViewDetails,
    onViewStats,
    viewMode = "grid",
}) => {
    // Portal-based menu state: null = closed, object = { top|bottom, right } fixed coords
    const [menuPos, setMenuPos] = useState(null);
    const menuBtnRef = useRef(null);

    const openMenu = useCallback((e) => {
        e.stopPropagation();
        if (menuPos) { setMenuPos(null); return; }
        const rect = menuBtnRef.current?.getBoundingClientRect();
        if (!rect) return;
        const MENU_H = 200; // estimated menu height
        const spaceBelow = window.innerHeight - rect.bottom;
        setMenuPos(
            spaceBelow >= MENU_H
                ? { top: rect.bottom + 6, right: window.innerWidth - rect.right }
                : { bottom: window.innerHeight - rect.top + 6, right: window.innerWidth - rect.right }
        );
    }, [menuPos]);

    const closeMenu = useCallback(() => setMenuPos(null), []);

    // Close on scroll/resize so fixed coords don't drift
    useEffect(() => {
        if (!menuPos) return;
        const close = () => setMenuPos(null);
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        return () => {
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
        };
    }, [menuPos]);

    const menuItems = [
        { label: "View Stats", icon: BarChart3, color: "text-indigo-500 dark:text-indigo-400", action: () => { onViewStats?.(campaign); closeMenu(); } },
        { label: "View Details", icon: Info, color: "text-indigo-500 dark:text-indigo-400", action: () => { onViewDetails?.(campaign); closeMenu(); } },
        { label: "Edit Campaign", icon: Edit, color: "text-indigo-500 dark:text-indigo-400", action: () => { onEdit(campaign); closeMenu(); } },
    ];

    // Portal context menu — fixed to viewport, never causes overflow/layout shift
    const contextMenu = menuPos ? createPortal(
        <>
            {/* Invisible full-screen backdrop */}
            <div
                className="fixed inset-0 z-[9990]"
                onClick={(e) => { e.stopPropagation(); closeMenu(); }}
            />
            {/* Menu panel */}
            <div
                className="fixed z-[9991] w-52 bg-surface dark:bg-dark-800
                    rounded-xl shadow-2xl border border-line dark:border-dark-700
                    py-1.5 overflow-hidden"
                style={{
                    top: menuPos.top ?? "auto",
                    bottom: menuPos.bottom ?? "auto",
                    right: menuPos.right,
                    animation: "fadeIn 0.12s ease both",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {menuItems.map(({ label, icon: Icon, color, action }) => (
                    <button
                        key={label}
                        onClick={(e) => { e.stopPropagation(); action(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left
                            text-[13px] font-medium text-gray-700 dark:text-gray-300
                            hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                    >
                        <Icon className={`w-3.5 h-3.5 ${color}`} />
                        {label}
                    </button>
                ))}
                <div className="my-1 mx-2 border-t border-line-subtle dark:border-dark-700" />
                <button
                    onClick={(e) => { e.stopPropagation(); onDelete(campaign); closeMenu(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left
                        text-[13px] font-medium text-red-600 dark:text-red-400
                        hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete Campaign
                </button>
            </div>
        </>,
        document.body
    ) : null;

    const campaignName = campaign?.name || "Untitled Campaign";
    const campaignStatus = campaign?.c_status || "";
    const templateId = campaign?.temp_id || null;
    const templateName = campaign?.temp_name || "—";
    const startDate = campaign?.s_date || null;
    const endDate = campaign?.e_date || null;
    const emailUsed = campaign?.email_used || "—";
    const creditsUsed = campaign?.credits_used || 0;
    const groupId = campaign?.c_grp || null;

    const style = getStatusStyle(campaignStatus);

    if (viewMode === "grid") {
        return (
            <div
                onClick={() => onViewStats?.(campaign)}
                className={`relative overflow-hidden rounded-xl border transition-all duration-200 group cursor-pointer
                    ${isSelected
                        ? "bg-surface dark:bg-dark-800 border-blue-500 dark:border-blue-500 ring-2 ring-blue-500/20 shadow-md"
                        : "bg-surface dark:bg-dark-800 border-line dark:border-dark-700 shadow-sm hover:shadow-lg hover:-translate-y-0.5 hover:border-line-strong dark:hover:border-dark-600"
                    }`}
            >
                <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${style.bar}`} />

                <div className="px-4 pt-4 pb-3">
                    <div className="flex items-start gap-2.5">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={onSelect}
                            onClick={(e) => e.stopPropagation()}
                            className="flex-shrink-0 mt-0.5 w-4 h-4 rounded border-line-strong dark:border-dark-500 text-blue-600 dark:text-blue-300 focus:ring-2 focus:ring-blue-500/20 cursor-pointer accent-blue-600"
                        />
                        <span className={`flex-shrink-0 mt-1.5 w-2 h-2 rounded-full ${style.dot}`} />
                        <div className="flex-1 min-w-0">
                            <h4 className="text-ui font-bold text-content dark:text-white truncate leading-snug">{campaignName}</h4>
                            <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide uppercase ${style.badge}`}>
                                {campaignStatus}
                            </span>
                        </div>

                        <div
                            className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => onViewStats?.(campaign)}
                                className="p-1.5 rounded-lg text-content-subtle dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                title="View Stats"
                            >
                                <BarChart3 className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={() => onEdit(campaign)}
                                className="p-1.5 rounded-lg text-content-subtle dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                title="Edit"
                            >
                                <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={() => onDelete(campaign)}
                                className="p-1.5 rounded-lg text-content-subtle dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                title="Delete"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>

                    <div className="mt-2.5 flex items-center gap-1.5">
                        <Mail className="w-3 h-3 text-content-subtle dark:text-gray-500 flex-shrink-0" />
                        <span className="text-meta font-medium text-content-muted dark:text-gray-400 truncate">{emailUsed}</span>
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-line-subtle dark:border-dark-700/60 bg-gray-50/50 dark:bg-transparent grid grid-cols-2 gap-x-4 gap-y-2.5">
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-content-subtle dark:text-gray-500 mb-0.5">Template</p>
                        <p className="text-meta font-semibold text-content dark:text-white truncate" title={templateName}>{templateName}</p>
                        {templateId && <p className="text-[11px] font-medium text-content-subtle dark:text-gray-500 mt-0.5">ID: {templateId}</p>}
                    </div>
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-content-subtle dark:text-gray-500 mb-0.5">Credits Used</p>
                        <p className="text-[16px] font-extrabold text-content dark:text-white leading-none">{formatNumber(creditsUsed)}</p>
                    </div>
                    {groupId && (
                        <div className="col-span-2">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-content-subtle dark:text-gray-500 mb-0.5">Group</p>
                            <p className="text-meta font-semibold text-content dark:text-white truncate">{groupId}</p>
                        </div>
                    )}
                </div>

                <div className="px-4 py-2.5 bg-gray-50/70 dark:bg-dark-700/30 border-t border-line-subtle dark:border-dark-700">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-content-muted dark:text-gray-400">
                            <Calendar className="w-3 h-3 flex-shrink-0" />
                            <span>{formatDate(startDate)}</span>
                        </div>
                        <ArrowRight className="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-content-muted dark:text-gray-400">
                            <Calendar className="w-3 h-3 flex-shrink-0" />
                            <span>{formatDate(endDate)}</span>
                        </div>
                    </div>

                    <div className="mt-1.5 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <BarChart3 className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                        <span className="text-[11px] font-semibold text-blue-500 dark:text-blue-400">Click to view stats</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            onClick={() => onViewStats?.(campaign)}
            className={`relative rounded-xl border transition-all duration-200 group cursor-pointer
                ${isSelected
                    ? "bg-surface dark:bg-dark-800 border-blue-500 dark:border-blue-500 ring-2 ring-blue-500/20 shadow-sm"
                    : "bg-surface dark:bg-dark-800 border-line dark:border-dark-700 hover:shadow-md hover:border-line-strong dark:hover:border-dark-600"
                }`}
        >
            {/* Left accent bar */}
            <div className={`absolute top-0 left-0 bottom-0 w-[3px] bg-gradient-to-b ${style.bar}`} />

            <div className={`${LIST_ROW_GRID_CLASS} py-3`}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={onSelect}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0 w-4 h-4 rounded border-line-strong dark:border-dark-500 text-blue-600 dark:text-blue-300 focus:ring-2 focus:ring-blue-500/20 cursor-pointer accent-blue-600"
                />
                <span className={`flex-shrink-0 w-2 h-2 rounded-full ${style.dot}`} />

                {/* Name + badge + email */}
                <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap sm:flex-nowrap">
                        {/* text-ui-lg: the campaign name is the row's identifier
                            and was the third-heaviest thing in it — the credits
                            figure below rendered at 14px extrabold and the name
                            at 13px, so a secondary column outweighed the subject
                            of the row. */}
                        <h4 className="min-w-0 truncate text-ui-lg font-semibold text-content dark:text-white leading-tight" title={campaignName}>
                            {campaignName}
                        </h4>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wide uppercase flex-shrink-0 ${style.badge}`}>
                            {campaignStatus}
                        </span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5">
                        <Mail className="w-3 h-3 text-content-subtle dark:text-gray-500 flex-shrink-0" />
                        <span className="text-meta font-medium text-content-muted dark:text-gray-400 truncate">{emailUsed}</span>
                    </div>
                </div>

                {/* Template (sm+) */}
                <div className="hidden sm:block min-w-0">
                    <p className="text-meta font-semibold text-content dark:text-white truncate text-center" title={templateName}>{templateName}</p>
                </div>

                {/* Credits (md+) — right-aligned and tabular so a column of
                    figures lines up on its digits and can be compared by eye.
                    Centred proportional numerals cannot be scanned. */}
                <div className="hidden md:block min-w-0">
                    <p className="text-ui font-semibold text-content dark:text-white text-right tabular-nums pr-1">{formatNumber(creditsUsed)}</p>
                </div>

                {/* Dates (lg+) */}
                <div className="hidden xl:flex items-center justify-center gap-2 min-w-0 text-meta font-semibold text-content-muted dark:text-gray-400 whitespace-nowrap">
                    <Calendar className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{formatDate(startDate)}</span>
                    <ArrowRight className="w-3 h-3 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                    <span className="truncate">{formatDate(endDate)}</span>
                </div>

                {/* Context menu — portal-based, never causes overflow/layout shift */}
                <div className="justify-self-center" onClick={(e) => e.stopPropagation()}>
                    <button
                        ref={menuBtnRef}
                        onClick={openMenu}
                        className="p-1.5 rounded-lg text-content-subtle dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                        aria-label="More options"
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>
                    {contextMenu}
                </div>

                <div className="col-start-3 col-span-2 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 min-w-0 text-[11px] font-medium text-content-muted dark:text-gray-400 sm:col-span-3 md:col-span-4 xl:hidden">
                    <span className="min-w-0 truncate sm:hidden" title={templateName}>
                        {templateName}
                    </span>
                    <span className="whitespace-nowrap md:hidden text-gray-700 dark:text-gray-300">
                        {formatNumber(creditsUsed)} credits
                    </span>
                    <span className="col-span-2 flex items-center gap-1 whitespace-nowrap sm:col-span-1">
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                        {formatDate(startDate)}
                        {endDate && (
                            <>
                                <ArrowRight className="w-3 h-3 flex-shrink-0 text-gray-300 dark:text-gray-600" />
                                {formatDate(endDate)}
                            </>
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};
